export const MAX_SIDE = 1536;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't open that photo. Please try another one."));
    img.src = src;
  });
}

/** Resize (longest side ≤ 1536px) + JPEG 0.7 in the browser before any upload. Accepts a File, a data URL or a canvas. */
export async function prepareUpload(source) {
  let src = source;
  let revoke = null;
  if (source instanceof Blob) {
    if (source.type && !source.type.startsWith("image/")) throw new Error("That file isn't a photo. Please choose a JPG, PNG or HEIC image.");
    src = URL.createObjectURL(source);
    revoke = src;
  }
  try {
    const el = source instanceof HTMLCanvasElement ? source : await loadImage(src);
    const w = el.naturalWidth || el.width;
    const h = el.naturalHeight || el.height;
    if (!w || !h) throw new Error("Couldn't open that photo. Please try another one.");
    const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d").drawImage(el, 0, 0, canvas.width, canvas.height);
    const uri = canvas.toDataURL("image/jpeg", 0.7);
    return { uri, base64: uri.split(",")[1] };
  } finally {
    if (revoke) URL.revokeObjectURL(revoke);
  }
}

/** Opens the browser file chooser. Resolves {status: picked|cancelled|error}. */
export function pickFromFiles() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    let done = false;
    const finish = (r) => {
      if (done) return;
      done = true;
      input.remove();
      resolve(r);
    };
    input.addEventListener("change", async () => {
      const f = input.files?.[0];
      if (!f) return finish({ status: "cancelled" });
      try {
        const p = await prepareUpload(f);
        finish({ status: "picked", ...p });
      } catch (e) {
        finish({ status: "error", message: e?.message || "Couldn't open that photo. Please try another one." });
      }
    });
    input.addEventListener("cancel", () => finish({ status: "cancelled" }));
    document.body.appendChild(input);
    input.click();
  });
}
